<template>
  <div class="ri-card">
    <bill-card class="mb-2" :height="height">
      <div v-if="isPercentMode" class="ri-card-body">
        <a-progress
          type="circle"
          :percent="percentValue"
          :width="80"
          :strokeWidth="10"
          :strokeColor="progressColor"
          :showInfo="false" />
        <div class="meta">
          <div class="title">
            <slot name="title" />
            <span v-if="!$slots.title">{{ title }}</span>
          </div>
          <div class="percent" :style="{ color: progressColor }">{{ percentText }}</div>
        </div>
      </div>
      <template v-else>
        <div class="title d-flex align-items-center">
          <div class="flex-fill">
            <slot name="title" />
            <span v-if="!$slots.title">{{ title }}</span>
          </div>
        </div>
        <div v-if="isDefaultFormat" class="clearfix value-wrap">
          <a-tooltip v-if="tooltip">
            <template slot="title">{{ tooltip }}</template>
            <span :class="numberStyle" v-if="!valueIsNil">{{ value }}</span>
          </a-tooltip>
          <template v-else>
            <span :class="numberStyle" v-if="!valueIsNil">{{ value }}</span>
          </template>
          <span class="unit" v-if="showUnit">{{ unit }}</span>
        </div>
      </template>
      <template #customizeContent>
        <slot name="customizeContent" />
      </template>
    </bill-card>
  </div>
</template>

<script>
import * as R from 'ramda'
import BillCard from '@Bill/components/BillCard'
import i18n from '@/locales'

const DEFAULT_COLORS = ['rgb(245, 34, 45)', 'rgb(250, 173, 20)', '#52c41a']
const DEFAULT_RANGES = [60, 90]

export default {
  name: 'RiCard',
  components: {
    BillCard,
  },
  props: {
    title: String,
    tooltip: String,
    value: [String, Number],
    height: String,
    unit: {
      type: String,
      default: i18n.t('bill.text_29'),
    },
    showUnit: {
      type: Boolean,
      default: true,
    },
    isDefaultFormat: {
      type: Boolean,
      default: true,
    },
    numberFontColor: {
      type: String,
    },
    colors: {
      type: Array,
      default: () => DEFAULT_COLORS,
    },
    ranges: {
      type: Array,
      default: () => DEFAULT_RANGES,
    },
  },
  computed: {
    isPercentMode () {
      return !this.showUnit
    },
    valueIsNil () {
      return R.isNil(this.value)
    },
    percentValue () {
      if (this.valueIsNil) return 0
      if (typeof this.value === 'number') return this.value
      const str = String(this.value).replace('%', '').trim()
      const n = parseFloat(str)
      return Number.isNaN(n) ? 0 : Math.min(Math.max(n, 0), 100)
    },
    percentText () {
      return `${this.percentValue.toFixed(2)}%`
    },
    progressColor () {
      const percentage = this.percentValue
      for (let i = 0; i < this.ranges.length; i++) {
        if (i === 0 && percentage <= this.ranges[0]) {
          return this.colors[0]
        }
        if (i === this.ranges.length - 1 && percentage > this.ranges[this.ranges.length - 1]) {
          return this.colors[i + 1]
        }
        if (percentage > this.ranges[i - 1] && percentage <= this.ranges[i]) {
          return this.colors[i]
        }
      }
      return this.colors[0]
    },
    numberStyle () {
      const isUsedDefault = Boolean(this.numberFontColor)
      const obj = {
        num: true,
        'number-default-color': !isUsedDefault,
      }
      if (isUsedDefault) {
        obj[this.numberFontColor] = true
      }
      return obj
    },
  },
}
</script>

<style lang="less" scoped>
.ri-card {
  .ri-card-body {
    display: flex;
    align-items: center;
    padding-top: 4px;
  }
  .meta {
    flex: 1;
    min-width: 0;
    padding-left: 16px;
  }
  .title {
    font-size: 12px;
    color: #4c6072;
    line-height: 1.5;
    margin-bottom: 6px;
  }
  .percent {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
  }
  .value-wrap {
    height: 74px;
  }
  .num {
    float: left;
    height: 100%;
    line-height: 74px;
    font-size: 32px;
    font-weight: bold;
  }
  .number-default-color {
    color: #4da1ff;
  }
  .unit {
    float: right;
    color: #4c6072;
    font-size: 12px;
    height: 100%;
    line-height: 84px;
  }
}
</style>
